import { Button, Card, CardBody, CardHeader, Skeleton } from "@heroui/react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useFetchData } from "@/hooks";
import { EmptyState, ErrorState, ThemedText, PageHeader } from "@/components/ui";
import { useUser } from "@/context/UserContext";
import { LuArrowLeft, LuCircleUserRound, LuKey, LuPencil, LuShieldOff } from "react-icons/lu";

const RoleDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useUser();

    const { data, isLoading, error, refetch } = useFetchData(`roles/${id}/show`);

    if (error) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data hak akses."
                onAction={refetch}
            />
        );
    }

    const roleName = data?.name || "";
    const assignedGroups = data?.groups ?? [];
    const totalAssigned = data?.permissions_count ?? 0;
    const usersData = data?.users ?? [];

    return (
        <div className="mx-auto flex max-w-3xl flex-col gap-8">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex gap-4">
                    <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                        <LuArrowLeft size={20} />
                    </Button>
                    <PageHeader
                        title={roleName || "Detail Hak Akses"}
                        breadcrumbs={[
                            { label: "Dashboard", href: "/" },
                            { label: "HR" },
                            { label: "Hak Akses", href: "/role" },
                            { label: "Detail" },
                        ]}
                    />
                </div>
                <div className="flex shrink-0 gap-2 self-start sm:self-auto">
                    {hasPermission("role.edit") && (
                        <Button
                            as={Link}
                            to={`/role/${id}/permissions`}
                            variant="flat"
                            size="sm"
                            startContent={<LuKey size={14} />}
                        >
                            Kelola Permission
                        </Button>
                    )}
                    {hasPermission("role.edit") && (
                        <Button
                            as={Link}
                            to={`/role/${id}/edit`}
                            color="primary"
                            size="sm"
                            startContent={<LuPencil size={14} />}
                        >
                            Edit
                        </Button>
                    )}
                </div>
            </div>

            {isLoading ? (
                <>
                    {/* Info Skeleton */}
                    <Card className="border-default-200 border" shadow="none">
                        <CardHeader className="bg-default-100/70 px-6 py-4">
                            <Skeleton className="h-5 w-40 rounded-lg" />
                        </CardHeader>
                        <CardBody className="px-6 py-4">
                            <div className="flex flex-col gap-3">
                                {[...Array(2)].map((_, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <Skeleton className="h-4 w-36 shrink-0 rounded-lg" />
                                        <Skeleton className="h-4 w-32 rounded-lg" />
                                    </div>
                                ))}
                            </div>
                        </CardBody>
                    </Card>

                    {/* Permissions Skeleton */}
                    <Card className="border-default-200 border" shadow="none">
                        <CardHeader className="bg-default-100/70 px-6 py-4">
                            <Skeleton className="h-5 w-48 rounded-lg" />
                        </CardHeader>
                        <CardBody className="divide-default-100 divide-y p-0">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="flex flex-col gap-1 px-6 py-3 sm:flex-row sm:gap-6">
                                    <Skeleton className="h-4 w-36 shrink-0 rounded-lg" />
                                    <Skeleton className="h-4 w-48 rounded-lg" />
                                </div>
                            ))}
                        </CardBody>
                    </Card>

                    {/* Users Skeleton */}
                    <Card className="border-default-200 border" shadow="none">
                        <CardHeader className="bg-default-100/70 px-6 py-4">
                            <Skeleton className="h-5 w-52 rounded-lg" />
                        </CardHeader>
                        <CardBody className="divide-default-100 divide-y p-0">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="flex flex-col gap-1 px-6 py-3 sm:flex-row sm:items-center sm:gap-6">
                                    <Skeleton className="h-4 w-36 shrink-0 rounded-lg" />
                                    <Skeleton className="h-4 w-48 rounded-lg" />
                                </div>
                            ))}
                        </CardBody>
                    </Card>
                </>
            ) : (
                <>
                    {/* Info Card */}
                    <Card className="border-default-200 border" shadow="none">
                        <CardHeader className="bg-default-100/70 px-6 py-4">
                            <ThemedText className="font-semibold">Informasi Hak Akses</ThemedText>
                        </CardHeader>
                        <CardBody className="px-6 py-4">
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-3">
                                    <ThemedText size="sm" variant="secondary" className="w-40 shrink-0">
                                        Nama
                                    </ThemedText>
                                    <ThemedText size="sm">{roleName}</ThemedText>
                                </div>
                                <div className="flex items-center gap-3">
                                    <ThemedText size="sm" variant="secondary" className="w-40 shrink-0">
                                        Jumlah Permission
                                    </ThemedText>
                                    <ThemedText size="sm">
                                        <span className="font-medium">{totalAssigned}</span> permission
                                    </ThemedText>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Permissions Table */}
                    <Card className="border-default-200 border" shadow="none">
                        <CardHeader className="bg-default-100/70 px-6 py-4">
                            <ThemedText className="font-semibold">Permission yang Diberikan</ThemedText>
                        </CardHeader>
                        <CardBody className="p-0">
                            {!assignedGroups?.length ? (
                                <EmptyState
                                    title="Belum Ada Permission"
                                    description="Role ini belum memiliki permission. Gunakan Kelola Permission untuk mengatur."
                                    icon={LuShieldOff}
                                />
                            ) : (
                                <div className="divide-default-100 divide-y">
                                    {assignedGroups.map((group, index) => (
                                        <div
                                            key={group.key}
                                            className={`flex flex-col gap-1 px-6 py-3 sm:flex-row sm:gap-6 ${index % 2 === 0 ? "" : "bg-default-50/50"}`}
                                        >
                                            <ThemedText
                                                size="sm"
                                                className="sm:w-52 sm:shrink-0 font-medium"
                                            >
                                                {group.label}
                                            </ThemedText>
                                            <ThemedText size="sm" variant="secondary">
                                                {group.permissions.map((p) => p.label).join(", ")}
                                            </ThemedText>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    {/* Users */}
                    <Card className="border-default-200 border" shadow="none">
                        <CardHeader className="bg-default-100/70 px-6 py-4">
                            <ThemedText className="font-semibold">Pengguna dengan Hak Akses Ini</ThemedText>
                        </CardHeader>
                        <CardBody className="p-0">
                            {!usersData?.length ? (
                                <EmptyState
                                    title="Belum Ada Pengguna"
                                    description="Belum ada pengguna yang menggunakan hak akses ini."
                                    icon={LuCircleUserRound}
                                />
                            ) : (
                                <div className="divide-default-100 divide-y">
                                    {usersData.map((user, index) => (
                                        <div
                                            key={user.id}
                                            className={`flex flex-col gap-1 px-6 py-3 sm:flex-row sm:items-center sm:gap-6 ${index % 2 === 0 ? "" : "bg-default-50/50"}`}
                                        >
                                            <ThemedText size="sm" className="sm:w-52 sm:shrink-0 font-medium">
                                                {user.name}
                                            </ThemedText>
                                            <ThemedText size="sm" variant="secondary">
                                                {user.email}
                                            </ThemedText>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </>
            )}
        </div>
    );
};

export default RoleDetail;
